import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

interface IProps {
  closeFilterModal: () => void;
  selectNonActionableFilterAndValue: any;
  setSelectNonActionableFilterAndValue: any;
  handleSubmit: (selectNonActionableFilterAndValueValues: any) => void;
}

const NonActionableFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectNonActionableFilterAndValue,
  setSelectNonActionableFilterAndValue,
  handleSubmit,
}) => {
  const [selectNonActionableFilters, setSelectNonActionableFilters] = useState<string[]>(
    []
  );
  const [dateTo, setDateTo] = useState<string>("");
  const [suggestions, setSuggestions] = useState([]);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);
  const [error, setError] = useState<string | null>(null);

  const filters = [
    { label: "Customer email", value: "action__contact__email__icontains" },
    { label: "Agent name", value: "action__assignee__username__icontains" },
    { label: "Subject", value: "original_subject__icontains" },
    { label: "Date", value: "created" },
  ];

  // Load filter state from sessionStorage when the component mounts
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectNonActionableFilters");
    const savedFilterValues = sessionStorage.getItem(
      "selectNonActionableFilterAndValue"
    );

    if (savedFilters && savedFilterValues) {
      const parsedFilters = savedFilters && savedFilters !== "undefined" ? JSON.parse(savedFilters) : [];
      const parsedValues = savedFilterValues && savedFilterValues !== "undefined" ? JSON.parse(savedFilterValues) : {};
      
      setSelectNonActionableFilters(parsedFilters);
      setSelectNonActionableFilterAndValue(parsedValues);
      setDateTo(parsedValues.created || "");
    } else {
      const appliedFilters = Object.keys(selectNonActionableFilterAndValue ?? {}).filter(
        (key) => (selectNonActionableFilterAndValue ?? {})[key]
      );
      setSelectNonActionableFilters(appliedFilters);
      setDateTo(selectNonActionableFilterAndValue?.created || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filter state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem(
      "selectNonActionableFilters",
      JSON.stringify(selectNonActionableFilters)
    );
    sessionStorage.setItem(
      "selectNonActionableFilterAndValue",
      JSON.stringify(selectNonActionableFilterAndValue)
    );
  }, [selectNonActionableFilters, selectNonActionableFilterAndValue]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectNonActionableFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectNonActionableFilterAndValue((prev) => ({
          ...prev,
          [value]: "",
        }));
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      handleSubmit(
        updatedFilters.reduce((acc, filter) => {
          acc[filter] = (selectNonActionableFilterAndValue ?? {})[filter] || "";
          return acc;
        }, {})
      );
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectNonActionableFilterAndValue((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectNonActionableFilterAndValue((prev) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectNonActionableFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {});
    setSelectNonActionableFilterAndValue(emptyFilters);
    setDateTo("");
    setError(null);
    sessionStorage.removeItem("selectNonActionableFilters");
    sessionStorage.removeItem("selectNonActionableFilterAndValue");
    handleSubmit({});
  };

  const isFilterApplied = () =>
    selectNonActionableFilters.some(
      (filter) =>
        (filter === "created" && dateTo) || selectNonActionableFilterAndValue[filter]
    );

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };

  const searchAssigneeSuggestions = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/users/users/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { username__icontains: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setAssigneeSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch assignee suggestions.");
      });
  };

  const handleApplyFilters = () => {
    handleSubmit(selectNonActionableFilterAndValue);
    closeFilterModal();
  };

  return (
    <Modal show={true} onHide={closeFilterModal} centered backdrop="static">
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters - Non-Actionable</Modal.Title>
        <CloseButton onClick={closeFilterModal} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>} {/* Display error */}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectNonActionableFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectNonActionableFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            style: { textTransform: "uppercase" },
                          },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "action__contact__email__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={selectNonActionableFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );

                          setSelectNonActionableFilterAndValue((prev) => ({
                            ...prev,
                            action__contact__email__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.email
                                : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : filter.value ===
                      "action__assignee__username__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={assigneeSuggestions.map(
                          (option) => option.username
                        )}
                        value={selectNonActionableFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchAssigneeSuggestions(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = assigneeSuggestions.find(
                            (suggestion) => suggestion.username === newValue
                          );

                          setSelectNonActionableFilterAndValue((prev) => ({
                            ...prev,
                            action__assignee__username__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.username
                                : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={selectNonActionableFilterAndValue[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button type="button" variant="primary" onClick={handleApplyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NonActionableFilterModal;